import { useLocalization } from '@lib/localization';
import { type ReportPreviewSource } from '@store/api/reportDesignEndpoints';
import { useState } from 'react';

/** A real inspection chosen as what a preview is drawn from, with what the picker shows for it. */
export interface PreviewInspection extends ReportPreviewSource {
  name: string;
  projectName: string;
}

/** What a preview is drawn from and in which language, and when it is drawn again. */
export interface PreviewSourceControl {
  /** The inspection chosen in the picker, or `null` for the built-in sample. */
  inspection: PreviewInspection | null;
  /** The language the report is drawn in: one of the interface's languages. */
  locale: string;
  /** What to hand `PdfPreview` as its `revision`. */
  revision: string;
  /**
   * What a request sent now is drawn from; the sample while it is `undefined`. It stays the
   * sample until Refresh has been pressed for a chosen inspection, and is then the inspection
   * Refresh was last pressed for, which is not the chosen one once another has been picked.
   */
  source: ReportPreviewSource | undefined;
  /** True while what Refresh would draw is not what was last asked for. Only with a real inspection. */
  pending: boolean;
  chooseInspection: (inspection: PreviewInspection | null) => void;
  chooseLocale: (locale: string) => void;
  refresh: () => void;
}

/**
 * The state behind `PreviewSource`: which data a preview is drawn from, and the revision
 * that makes `PdfPreview` draw it.
 *
 * `live` is everything else the picture depends on, as text: the draft, usually. With the
 * sample inspection the revision follows it, so the preview follows the author. With a real
 * inspection it does not: such a report can run to hundreds of pages, the server draws one
 * preview a user at a time, and a drawing that was started is not stopped. So the revision
 * then stands still, through edits, a change of language and the choice of the inspection
 * itself, and moves only when Refresh is pressed.
 *
 * The inspection a request draws stands still with it: `source` is the one Refresh was
 * pressed for, whichever is chosen since. The language and the caller's own draft are sent
 * as they are when a request goes out, so the caller hands `pending` to `PdfPreview` as
 * `stale`, and nothing is asked while they are not what the revision was made for.
 */
export const usePreviewSource = (live: string): PreviewSourceControl => {
  const { currentLanguage } = useLocalization();

  const [inspection, setInspection] = useState<PreviewInspection | null>(null);
  const [locale, setLocale] = useState(currentLanguage);
  const [refreshes, setRefreshes] = useState(0);
  // While a real inspection is chosen: the revision that stands still, and what it was made
  // for. `target` is null and `source` the sample until Refresh is first pressed for one;
  // from then on they are those of the inspection it was last pressed for.
  const [held, setHeld] = useState<{
    revision: string;
    target: string | null;
    source: ReportPreviewSource | undefined;
  } | null>(null);

  const liveRevision = `${refreshes}\n${locale}\n${live}`;
  const target = inspection ? `${inspection.projectId}/${inspection.inspectionId}\n${locale}\n${live}` : null;

  return {
    inspection,
    locale,
    revision: inspection && held ? held.revision : liveRevision,
    // Not the chosen inspection: a request that is still owed when another one is picked
    // would draw that one unasked, and its picture would be kept as this revision's.
    source: inspection && held ? held.source : undefined,
    pending: inspection !== null && held?.target !== target,
    chooseInspection: (next) => {
      // Choosing an inspection draws nothing by itself: the revision is kept as it is at
      // this moment. Going back to the sample lets it follow the draft again.
      if (next && !inspection) setHeld({ revision: liveRevision, target: null, source: undefined });
      if (!next) setHeld(null);

      setInspection(next);
    },
    chooseLocale: setLocale,
    refresh: () => {
      setRefreshes(refreshes + 1);

      if (inspection && target) {
        setHeld({
          revision: `${refreshes + 1}\n${target}`,
          target,
          source: { projectId: inspection.projectId, inspectionId: inspection.inspectionId },
        });
      }
    },
  };
};
